import {tokensForThemeProvider} from '@forecast-it/design-system';
import React, {useLayoutEffect, useRef} from 'react';
import {useIntl} from 'react-intl';
import styled, {css, keyframes} from 'styled-components';
import {dispatch, EVENT_ID} from '../../../../containers/event_manager';
import type {ControlState, DisabledReason} from './controlState';
import {anchorElementId, novaInstanceKey} from './NovaAgentPicker';
import {RUN_CAP} from './novaInvocationStore';

export interface InvocationControlProps {
	taskId: string;
	// See NovaAgentPickerProps.instanceKey (NovaAgentPicker.tsx) — must match whatever the
	// sibling agentPicker below was given, or the caret's id and the picker's toggle/lookup key
	// disagree and the picker can't find its own caret.
	instanceKey?: string;
	state: ControlState;
	onAction: () => void;
	onOpenOutput: () => void;
	onStop: () => void;
	agentPicker?: React.ReactNode; // rendered inline below the control when the picker (a later task) is mounted; undefined until then
}

const REASON_MESSAGE_ID: Record<DisabledReason, string> = {
	role: 'nova_invocation.control.reason_role',
	task_locked: 'nova_invocation.control.reason_task_locked',
	no_agent_for_role: 'nova_invocation.control.reason_no_agent_for_role',
	no_genai_access: 'nova_invocation.control.reason_no_genai_access',
	run_cap_reached: 'nova_invocation.control.reason_run_cap',
};

let disabledReasonIdCounter = 0;

function useDisabledReasonId(): string {
	const idRef = useRef<string>();
	if (!idRef.current) {
		disabledReasonIdCounter += 1;
		idRef.current = `nova-disabled-reason-${disabledReasonIdCounter}`;
	}
	return idRef.current;
}

// This control is a small tinted status pill (e.g. light-blue "Action", light-green pulsing
// "Ready"), not a standard filled/outlined button — no design-system Button emphasis produces
// that look, so it's built here from design tokens rather than composed from Button.
type ChipTone = 'blue' | 'green' | 'neutral';

// Optional chaining because this module loads transitively through the shared web-components
// barrel — unrelated test files that mock '@forecast-it/design-system' without providing
// tokensForThemeProvider would otherwise crash on import alone, before any test even runs.
const ref = tokensForThemeProvider?.sky?.ref?.color;
const sys = tokensForThemeProvider?.sky?.sys?.color;

const CHIP_TONE_COLORS: Record<ChipTone, {background: string; border: string; text: string}> = {
	blue: {background: ref?.blue['50'] ?? '', border: ref?.blue['100'] ?? '', text: ref?.blue['900'] ?? ''},
	green: {background: ref?.green['50'] ?? '', border: ref?.green['75'] ?? '', text: sys?.text.semantic.success ?? ''},
	neutral: {
		background: ref?.neutral['200'] ?? '',
		border: ref?.neutral['300'] ?? '',
		text: ref?.neutral['400'] ?? '',
	},
};

const pulse = keyframes`
	0%, 100% { opacity: 1; }
	50% { opacity: 0.45; }
`;

const spin = keyframes`
	to { transform: rotate(360deg); }
`;

const chipStyles = css<{$tone: ChipTone; $pulse: boolean}>`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	border-radius: 999px;
	font-family: inherit;
	font-size: 12px;
	line-height: 16px;
	font-weight: 500;
	padding: 5px 11px;
	border: 1px solid ${({$tone}) => CHIP_TONE_COLORS[$tone].border};
	background: ${({$tone}) => CHIP_TONE_COLORS[$tone].background};
	color: ${({$tone}) => CHIP_TONE_COLORS[$tone].text};
	${({$pulse}) =>
		$pulse &&
		css`
			animation: ${pulse} 2s infinite;
		`}
`;

const ChipButton = styled.button<{$tone: ChipTone; $pulse: boolean}>`
	${chipStyles}
	cursor: pointer;
	&:disabled {
		cursor: not-allowed;
	}
`;

// Reuses CHIP_TONE_COLORS.blue rather than reading ref.blue[...] directly — ref itself can be
// undefined (see the optional chaining above), and CHIP_TONE_COLORS already guards that.
const Spinner = styled.span`
	display: inline-block;
	flex: none;
	width: 12px;
	height: 12px;
	border-radius: 50%;
	border: 2px solid ${CHIP_TONE_COLORS.blue.border};
	border-top-color: ${CHIP_TONE_COLORS.blue.text};
	animation: ${spin} 0.7s linear infinite;
`;

const CaretButton = styled.button`
	display: inline-flex;
	flex: none;
	align-items: center;
	justify-content: center;
	width: 22px;
	height: 22px;
	padding: 0;
	border-radius: 999px;
	border: 1px solid ${CHIP_TONE_COLORS.blue.border};
	background: ${CHIP_TONE_COLORS.blue.background};
	color: ${CHIP_TONE_COLORS.blue.text};
	font-size: 10px;
	line-height: 14px;
	cursor: pointer;
`;

// Same circular shape as CaretButton — occupies the same slot the caret would, since the two
// never show at once (the caret disappears once a run starts, see showCaret below).
const StopButton = styled.button`
	display: inline-flex;
	flex: none;
	align-items: center;
	justify-content: center;
	width: 22px;
	height: 22px;
	padding: 0;
	border-radius: 999px;
	border: 1px solid ${CHIP_TONE_COLORS.blue.border};
	background: ${CHIP_TONE_COLORS.blue.background};
	cursor: pointer;
`;

const StopIcon = styled.span`
	display: inline-block;
	flex: none;
	width: 7px;
	height: 7px;
	border-radius: 1px;
	background: ${CHIP_TONE_COLORS.blue.text};
`;

const ChipRow = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 4px;
`;

// This region exists only so a screen reader announces the working transition (see the focus
// effect below) — it must never be visible. Standard "visually hidden" clip, not display:none,
// so it stays in the accessibility tree while taking no visual space.
const StatusRegion = styled.span`
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
`;

// Every state's own native `title` (see each ChipButton below) is the row's one explanatory
// surface — the mockup's row-level Nova pill has no separate always-visible banner text in any
// state, disabled or not: a full run-cap reads exactly like any other disabled reason (grayed
// pill, reason on hover), and a near-cap warning has no row treatment at all in the design — that
// banner belongs to the task-detail composer instead (no host for it in this codebase yet, same
// position as FailurePresentation.tsx).
export const NovaInvocationControl = (props: InvocationControlProps): JSX.Element => {
	const {state} = props;
	const {formatMessage} = useIntl();
	const disabledReasonId = useDisabledReasonId();
	const stopButtonRef = useRef<HTMLButtonElement>(null);

	// The Working pill renders disabled (see the 'working' case below) — a browser can't keep focus
	// on a button the instant it becomes disabled, so a keyboard user who just pressed Action would
	// otherwise lose focus to the document body the moment the row flips to Working. Moving it onto
	// the new stop button (see StopButton below) keeps a keyboard user's focus somewhere useful.
	// previousKindRef excludes the initial mount (a row that starts out already Working, e.g. on
	// page load) — only an actual action→working transition should steal focus.
	const previousKindRef = useRef<ControlState['kind'] | null>(null);
	// A favourited task on My Work mounts two instances of this control for the same taskId
	// (Draggable_card's card and Task_row's row — see each one's own instanceKey comment). Both
	// subscribe to the same store entry and both observe the same action→working transition, so
	// without this guard, clicking Action on one instance moves focus onto whichever instance's
	// effect happens to run last (React runs sibling effects in mount order), not necessarily the
	// one actually clicked. Set synchronously in the click handler below, read and cleared here —
	// only the instance whose own button raised this transition ever moves focus.
	const actionClickedHereRef = useRef(false);
	useLayoutEffect(() => {
		if (
			previousKindRef.current !== null &&
			previousKindRef.current !== 'working' &&
			state.kind === 'working' &&
			actionClickedHereRef.current
		) {
			stopButtonRef.current?.focus();
		}
		actionClickedHereRef.current = false;
		previousKindRef.current = state.kind;
	}, [state.kind]);

	let button: React.ReactNode;
	let statusText = '';
	let disabledReasonSpan: React.ReactNode = null;
	let showCaret = false;
	let showStop = false;

	switch (state.kind) {
		case 'disabled': {
			// Also the button's native title — a disabled reason is meant to be legible to every
			// user on hover, not just announced to a screen reader (see the mockup's row tooltip
			// on the capped/locked Action button). RUN_CAP is passed for every reason, not just
			// run_cap_reached - react-intl ignores an interpolation value a message doesn't use, and
			// this keeps the run-cap message honest if RUN_CAP ever changes, instead of a hardcoded
			// "3" silently going stale in five locales.
			const reasonText = formatMessage({id: REASON_MESSAGE_ID[state.reason]}, {count: RUN_CAP});
			button = (
				<ChipButton
					$tone="neutral"
					$pulse={false}
					disabled
					title={reasonText}
					aria-describedby={disabledReasonId}
					data-cy="nova-invocation-control"
				>
					{formatMessage({id: 'nova_invocation.control.invoke'})}
				</ChipButton>
			);
			// Visually hidden, same as StatusRegion below — aria-describedby (on the button above)
			// reads this for a screen reader regardless of visibility. The title above already
			// covers sighted users; this only needs to reach assistive tech.
			disabledReasonSpan = <StatusRegion id={disabledReasonId}>{reasonText}</StatusRegion>;
			break;
		}
		case 'working':
			button = (
				<ChipButton $tone="blue" $pulse={false} disabled data-cy="nova-invocation-control">
					<Spinner />
					{formatMessage({id: 'nova_invocation.control.working'})}
				</ChipButton>
			);
			statusText = formatMessage({id: 'nova_invocation.control.status_working'});
			showStop = true;
			break;
		case 'ready_or_continue':
			button = (
				<ChipButton
					$tone={state.read ? 'blue' : 'green'}
					$pulse={!state.read}
					onClick={props.onOpenOutput}
					title={formatMessage({
						id: state.read ? 'nova_invocation.control.tooltip_continue' : 'nova_invocation.control.tooltip_ready',
					})}
					data-cy="nova-invocation-control"
				>
					{formatMessage({id: state.read ? 'nova_invocation.control.continue' : 'nova_invocation.control.ready'})}
				</ChipButton>
			);
			break;
		case 'action':
			button = (
				<ChipButton
					$tone="blue"
					$pulse={false}
					onClick={() => {
						actionClickedHereRef.current = true;
						props.onAction();
					}}
					title={formatMessage({id: 'nova_invocation.control.tooltip_action'})}
					data-cy="nova-invocation-control"
				>
					{formatMessage({id: 'nova_invocation.control.invoke'})}
				</ChipButton>
			);
			showCaret = true;
			break;
		default: {
			const _exhaustive: never = state;
			void _exhaustive;
			button = (
				<ChipButton $tone="neutral" $pulse={false} disabled data-cy="nova-invocation-control">
					{formatMessage({id: 'nova_invocation.control.invoke'})}
				</ChipButton>
			);
		}
	}

	return (
		// position: relative so `agentPicker` (a later task's popover) has a defined anchor to
		// position:absolute against, regardless of whatever container each mounting surface
		// happens to wrap this control in.
		<span style={{position: 'relative', display: 'inline-flex', alignItems: 'center'}}>
			<ChipRow>
				{button}
				{showCaret && (
					<CaretButton
						aria-label={formatMessage({id: 'nova_invocation.control.choose_agent'})}
						onClick={() => dispatch(EVENT_ID.NOVA_TOGGLE_PICKER, novaInstanceKey(props.taskId, props.instanceKey))}
						data-cy="nova-invocation-caret"
						id={anchorElementId(props.taskId, props.instanceKey)}
					>
						▾
					</CaretButton>
				)}
				{showStop && (
					<StopButton
						ref={stopButtonRef}
						onClick={props.onStop}
						title={formatMessage({id: 'nova_invocation.control.stop'})}
						aria-label={formatMessage({id: 'nova_invocation.control.stop'})}
						data-cy="nova-invocation-stop"
					>
						<StopIcon />
					</StopButton>
				)}
			</ChipRow>
			<StatusRegion role="status">{statusText}</StatusRegion>
			{disabledReasonSpan}
			{state.kind === 'action' && props.agentPicker}
		</span>
	);
};
